import React from 'react';

// PreviewTabs — the tab strip across the top of a preview CONTAINER (Orders /
// RMAs / Credit Memos). One tab per staged preview instance, labeled "Order #1",
// "RMA #2", etc. (index-based so a gap in group numbers still reads 1/2/3). The
// ACTIVE tab is highlighted; clicking a tab switches which instance's editor shows
// below. Each tab carries a right-side × that REMOVES that preview instance from
// the thread (staging-only — never cancels a NetSuite doc).
//
// Presentational only: no data fetching. Used identically for all three preview
// types so the containers match the drawing.

export interface PreviewTabsProps {
  label: 'Order' | 'RMA' | 'CM';
  groups: number[];            // e.g. [1, 2, 3] (group numbers; may have gaps)
  active: number;              // the currently-shown group number
  onSelect: (group: number) => void;
  onRemove: (group: number) => void;  // the × — remove this preview instance
}

const LABEL_TEXT: Record<PreviewTabsProps['label'], string> = {
  Order: 'Order',
  RMA: 'RMA',
  CM: 'Credit Memo',
};

const PreviewTabs: React.FC<PreviewTabsProps> = ({ label, groups, active, onSelect, onRemove }) => {
  // Never render an empty strip; a container with one implicit tab shows [1].
  const tabs = groups && groups.length ? groups : [1];
  // A single tab with no siblings still gets an × (remove clears the container).
  const text = LABEL_TEXT[label];

  return (
    <div className="flex items-end gap-1 border-b border-gray-200 mb-2" role="tablist" aria-label={`${text} previews`}>
      {tabs.map((g, idx) => {
        const isActive = g === active;
        return (
          <div
            key={`${label}-tab-${g}`}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(g)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(g); }
              else if (e.key === 'ArrowRight' && idx < tabs.length - 1) { e.preventDefault(); onSelect(tabs[idx + 1]); }
              else if (e.key === 'ArrowLeft' && idx > 0) { e.preventDefault(); onSelect(tabs[idx - 1]); }
            }}
            className={
              'group flex items-center gap-1 pl-3 pr-2 py-1.5 text-sm rounded-t cursor-pointer select-none border border-b-0 -mb-px ' +
              (isActive
                ? 'bg-white border-gray-200 text-brand-700 font-semibold'
                : 'bg-gray-50 border-transparent text-gray-500 hover:text-brand-700 hover:bg-gray-100')
            }
          >
            <span>{`${text} #${idx + 1}`}</span>
            <button
              type="button"
              aria-label={`Remove ${text} #${idx + 1}`}
              title={`Remove ${text} #${idx + 1}`}
              onClick={(e) => { e.stopPropagation(); onRemove(g); }}
              className="ml-0.5 flex items-center justify-center w-4 h-4 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
            >
              <span aria-hidden className="leading-none text-base">&times;</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default PreviewTabs;
